/**
 * theme.Carousel — ES6 carousel for Blue Light LP sections.
 * Emits .rm-carousel-* classes for blue-light.css styling.
 */
(function () {
  "use strict";

  window.theme = window.theme || {};

  const RM = {
    initialized: "rm-carousel-initialized",
    variableWidth: "rm-carousel-variable-width",
    list: "rm-carousel-list",
    track: "rm-carousel-track",
    slide: "rm-carousel-slide",
    cloned: "rm-carousel-cloned",
    prev: "rm-carousel-prev",
    next: "rm-carousel-next",
    arrow: "rm-carousel-arrow",
    dots: "rm-carousel-dots",
    active: "rm-carousel-active",
    current: "rm-carousel-current",
    center: "rm-carousel-center",
    slideSelector: ".rm-carousel-slide",
    slideNotCloned: ".rm-carousel-slide:not(.rm-carousel-cloned)",
  };

  const DEFAULTS = {
    slidesToShow: 1,
    slidesToScroll: 1,
    infinite: false,
    arrows: true,
    dots: false,
    speed: 300,
    adaptiveHeight: false,
    centerMode: false,
    centerPadding: "0",
    variableWidth: false,
    draggable: true,
    swipe: true,
    mobileFirst: false,
    responsive: null,
    asNavFor: null,
    focusOnSelect: false,
    enableBelow: null,
    enableAbove: null,
  };

  const registry = new Map();

  function resolveElement(el) {
    if (!el) return null;
    if (typeof el === "string") return document.querySelector(el);
    return el;
  }

  function resolveElements(el) {
    if (!el) return [];
    if (typeof el === "string") return Array.from(document.querySelectorAll(el));
    return [el];
  }

  function registryKey(el) {
    if (typeof el === "string") return el;
    if (el.id) return "#" + el.id;
    if (!el.dataset.carouselId) {
      el.dataset.carouselId = "carousel-" + Math.random().toString(36).slice(2, 9);
    }
    return "[data-carousel-id=\"" + el.dataset.carouselId + "\"]";
  }

  function mergeSettings(base, width) {
    const opts = Object.assign({}, base);
    if (!opts.responsive || !opts.responsive.length) return opts;

    const sorted = opts.responsive.slice().sort((a, b) => a.breakpoint - b.breakpoint);

    if (opts.mobileFirst) {
      let active = Object.assign({}, opts);
      sorted.forEach((bp) => {
        if (width >= bp.breakpoint) {
          active = Object.assign({}, active, bp.settings || {});
        }
      });
      return active;
    }

    let active = Object.assign({}, opts);
    for (let i = sorted.length - 1; i >= 0; i--) {
      if (width <= sorted[i].breakpoint) {
        active = Object.assign({}, active, sorted[i].settings || {});
      }
    }
    return active;
  }

  function isEnabledForWidth(options, width) {
    if (options.enableBelow != null && width >= options.enableBelow) return false;
    if (options.enableAbove != null && width <= options.enableAbove) return false;
    return true;
  }

  class Carousel {
    constructor(element, options) {
      this.root = resolveElement(element);
      if (!this.root || this.root.classList.contains(RM.initialized)) return;

      this.userOptions = Object.assign({}, DEFAULTS, options || {});
      this.index = 0;
      this.slideElements = [];
      this.clones = [];
      this.isDragging = false;
      this.navPartner = null;

      const key = registryKey(this.root);
      registry.set(key, this);
      if (this.userOptions.asNavFor) {
        this.navSelector = this.userOptions.asNavFor;
      }

      this._onResize = this._onResize.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);

      this._maybeInit();
      window.addEventListener("resize", this._onResize);
    }

    _maybeInit() {
      const width = window.innerWidth;
      if (!isEnabledForWidth(this.userOptions, width)) {
        this._teardown(false);
        return;
      }
      if (!this.root.classList.contains(RM.initialized)) {
        this._build();
      } else {
        this.refresh();
      }
    }

    _build() {
      this.originalHTML = this.root.innerHTML;
      this.originalChildNodes = Array.from(this.root.children);

      this.settings = mergeSettings(this.userOptions, window.innerWidth);

      this.root.classList.add(RM.initialized, "carousel");
      if (this.settings.variableWidth) {
        this.root.classList.add(RM.variableWidth);
      }

      this.list = document.createElement("div");
      this.list.className = RM.list;
      if (this.settings.draggable !== false) this.list.classList.add("draggable");

      this.track = document.createElement("div");
      this.track.className = RM.track;

      this.slideElements = this.originalChildNodes.map((node, i) => {
        const slide = document.createElement("div");
        slide.className = RM.slide;
        slide.dataset.rmCarouselIndex = String(i);
        slide.appendChild(node);
        return slide;
      });

      // Liquid whitespace can leave text nodes behind after slides are moved out.
      this.root.replaceChildren();

      this._appendSlides();
      this.list.appendChild(this.track);
      this.root.appendChild(this.list);

      if (this.settings.arrows) this._buildArrows();
      if (this.settings.dots) this._buildDots();

      this._bindNav();
      this._bindDrag();
      this._applySlideWidths();
      this.goTo(this.index, true);

      if (!this.list.clientWidth) {
        requestAnimationFrame(() => {
          if (!this.track) return;
          this._applySlideWidths();
          this.goTo(this.index, true);
        });
      }

      this._registerSelectors();
      this._linkNavPartner();
    }

    _registerSelectors() {
      const key = registryKey(this.root);
      registry.set(key, this);
      this.root.classList.forEach((cls) => {
        if (cls.indexOf("slider-content") === 0 || cls.indexOf("slider-thumb") === 0) {
          registry.set("." + cls, this);
        }
      });
    }

    _appendSlides() {
      this.track.innerHTML = "";
      this.clones = [];

      if (this.settings.infinite && this.slideElements.length > 1) {
        const show = Math.min(this.settings.slidesToShow, this.slideElements.length);
        for (let i = this.slideElements.length - show; i < this.slideElements.length; i++) {
          const clone = this.slideElements[i].cloneNode(true);
          clone.classList.add(RM.cloned);
          this.clones.push(clone);
          this.track.appendChild(clone);
        }
      }

      this.slideElements.forEach((slide) => this.track.appendChild(slide));

      if (this.settings.infinite && this.slideElements.length > 1) {
        const show = Math.min(this.settings.slidesToShow, this.slideElements.length);
        for (let i = 0; i < show; i++) {
          const clone = this.slideElements[i].cloneNode(true);
          clone.classList.add(RM.cloned);
          this.clones.push(clone);
          this.track.appendChild(clone);
        }
      }
    }

    _buildArrows() {
      this.prevArrow = document.createElement("button");
      this.prevArrow.type = "button";
      this.prevArrow.className = RM.prev + " " + RM.arrow;
      this.prevArrow.setAttribute("aria-label", "Previous");
      this.prevArrow.addEventListener("click", () => this.prev());

      this.nextArrow = document.createElement("button");
      this.nextArrow.type = "button";
      this.nextArrow.className = RM.next + " " + RM.arrow;
      this.nextArrow.setAttribute("aria-label", "Next");
      this.nextArrow.addEventListener("click", () => this.next());

      this.root.appendChild(this.prevArrow);
      this.root.appendChild(this.nextArrow);
    }

    _buildDots() {
      this.dotsNav = document.createElement("ul");
      this.dotsNav.className = RM.dots;
      this.dotsNav.setAttribute("role", "tablist");
      const pageCount = this._pageCount();
      for (let i = 0; i < pageCount; i++) {
        const li = document.createElement("li");
        const btn = document.createElement("button");
        btn.type = "button";
        btn.textContent = String(i + 1);
        btn.addEventListener("click", () => this.goTo(i * this.settings.slidesToScroll));
        li.appendChild(btn);
        this.dotsNav.appendChild(li);
      }
      this.root.appendChild(this.dotsNav);
    }

    _rebuildControls() {
      if (this.prevArrow) {
        this.prevArrow.remove();
        this.prevArrow = null;
      }
      if (this.nextArrow) {
        this.nextArrow.remove();
        this.nextArrow = null;
      }
      if (this.dotsNav) {
        this.dotsNav.remove();
        this.dotsNav = null;
      }
      if (this.settings.arrows) this._buildArrows();
      if (this.settings.dots) this._buildDots();
    }

    _pageCount() {
      const len = this.slideElements.length;
      const scroll = this.settings.slidesToScroll || 1;
      return Math.max(1, Math.ceil((len - (this.settings.slidesToShow - 1)) / scroll));
    }

    _allSlides() {
      return Array.from(this.track.querySelectorAll(RM.slideNotCloned));
    }

    _bindNav() {
      if (!this.settings.focusOnSelect) return;
      this.track.addEventListener("click", (e) => {
        const slide = e.target.closest(RM.slideSelector);
        if (!slide) return;
        const idx = parseInt(slide.dataset.rmCarouselIndex, 10);
        if (!isNaN(idx)) {
          this.goTo(idx);
          if (this.navPartner) this.navPartner.goTo(idx);
        }
      });
    }

    _bindDrag() {
      if (this.settings.draggable === false && this.settings.swipe === false) return;
      this.list.addEventListener("pointerdown", this._onPointerDown);
    }

    _onPointerDown(e) {
      if (e.button !== 0) return;
      this.isDragging = true;
      this.dragStartX = e.clientX;
      this.dragStartTranslate = this._currentTranslate();
      this.list.setPointerCapture(e.pointerId);
      this.track.style.transition = "none";
    }

    _onPointerMove(e) {
      if (!this.isDragging) return;
      const dx = e.clientX - this.dragStartX;
      this._setTranslate(this.dragStartTranslate + dx);
    }

    _onPointerUp(e) {
      if (!this.isDragging) return;
      this.isDragging = false;
      try {
        this.list.releasePointerCapture(e.pointerId);
      } catch (err) {}
      const dx = e.clientX - this.dragStartX;
      this.track.style.transition = "";
      if (Math.abs(dx) > 40) {
        if (dx < 0) this.next();
        else this.prev();
      } else {
        this.goTo(this.index);
      }
    }

    _slideWidth() {
      if (!this.list) return 0;
      const listWidth = this.list.clientWidth;
      if (!listWidth) return 0;
      if (this.settings.variableWidth) {
        const slide = this.slideElements[this.index] || this.slideElements[0];
        return slide ? slide.offsetWidth : listWidth;
      }
      return listWidth / this.settings.slidesToShow;
    }

    _applySlideWidths() {
      if (!this.track || this.settings.variableWidth) return;
      const width = this._slideWidth();
      if (!width) return;
      const widthPx = width + "px";
      Array.from(this.track.querySelectorAll(RM.slideSelector)).forEach((slide) => {
        slide.style.width = widthPx;
        slide.style.flexBasis = widthPx;
      });
    }

    _currentTranslate() {
      const match = this.track.style.transform.match(/translate3d\((-?[\d.]+)px/);
      return match ? parseFloat(match[1]) : 0;
    }

    _setTranslate(px) {
      this.track.style.transform = "translate3d(" + px + "px, 0, 0)";
    }

    _offsetForIndex(index) {
      const slideW = this._slideWidth();
      let offset = index * slideW;

      if (this.settings.centerMode) {
        const listWidth = this.list.clientWidth;
        offset -= (listWidth - slideW) / 2;
      }

      if (this.settings.infinite && this.slideElements.length > 1) {
        const cloneCount = Math.min(this.settings.slidesToShow, this.slideElements.length);
        offset += cloneCount * slideW;
      }

      return -offset;
    }

    goTo(index, instant) {
      if (!this.track) return;
      const len = this.slideElements.length;
      if (!len) return;

      if (!this.list.clientWidth) {
        this._applySlideWidths();
      }

      let target = index;
      if (this.settings.infinite) {
        if (target < 0) target = len - 1;
        if (target >= len) target = 0;
      } else {
        target = Math.max(0, Math.min(target, len - this.settings.slidesToShow));
      }

      this.index = target;
      this._applySlideWidths();
      const px = this._offsetForIndex(target);

      this.track.style.transition = instant
        ? "none"
        : "transform " + this.settings.speed + "ms ease";

      this._setTranslate(px);
      this._updateClasses();
      this._updateAdaptiveHeight();

      if (this.navPartner && !this.navPartner._syncing) {
        this.navPartner._syncing = true;
        this.navPartner.goTo(target, instant);
        this.navPartner._syncing = false;
      }
    }

    _updateClasses() {
      const slides = Array.from(this.track.querySelectorAll(RM.slideSelector));
      slides.forEach((slide) => {
        slide.classList.remove(RM.current, RM.active, RM.center);
      });

      const show = this.settings.slidesToShow;
      const len = this.slideElements.length;
      let physicalStart = this.index;

      if (this.settings.infinite && len > 1) {
        const cloneCount = Math.min(show, len);
        physicalStart = cloneCount + this.index;
      }

      for (let i = 0; i < show; i++) {
        const slide = slides[physicalStart + i];
        if (!slide) continue;
        slide.classList.add(RM.active);
        if (i === 0) slide.classList.add(RM.current);
        if (this.settings.centerMode && i === 0) slide.classList.add(RM.center);
      }

      if (this.dotsNav) {
        const dotIndex = Math.floor(this.index / (this.settings.slidesToScroll || 1));
        Array.from(this.dotsNav.children).forEach((li, i) => {
          li.classList.toggle(RM.active, i === dotIndex);
        });
      }
    }

    _updateAdaptiveHeight() {
      if (!this.settings.adaptiveHeight || !this.list) return;
      const current = this.slideElements[this.index];
      if (current) {
        this.list.style.height = current.offsetHeight + "px";
      }
    }

    prev() {
      this.goTo(this.index - (this.settings.slidesToScroll || 1));
    }

    next() {
      this.goTo(this.index + (this.settings.slidesToScroll || 1));
    }

    refresh() {
      if (!this.root.classList.contains(RM.initialized)) {
        this._maybeInit();
        return;
      }

      const prevSettings = this.settings || {};
      this.settings = mergeSettings(this.userOptions, window.innerWidth);

      const controlsChanged =
        prevSettings.arrows !== this.settings.arrows ||
        prevSettings.dots !== this.settings.dots;

      if (controlsChanged) {
        this._rebuildControls();
      } else if (this.dotsNav && this.settings.dots) {
        const pageCount = this._pageCount();
        if (this.dotsNav.children.length !== pageCount) {
          this._rebuildControls();
        }
      }

      const len = this.slideElements.length;
      const maxIndex = Math.max(0, len - this.settings.slidesToShow);
      if (this.index > maxIndex) this.index = maxIndex;

      this._appendSlides();
      this._applySlideWidths();
      this.goTo(this.index, true);
    }

    _linkNavPartner() {
      if (!this.navSelector) return;
      const sel = this.navSelector.trim();
      let partner = registry.get(sel);
      if (!partner) {
        const el = document.querySelector(sel);
        if (el) partner = registry.get(registryKey(el));
      }
      if (partner && partner !== this) {
        this.navPartner = partner;
        partner.navPartner = this;
      }
    }

    _onResize() {
      const wasInit = this.root.classList.contains(RM.initialized);
      const shouldInit = isEnabledForWidth(this.userOptions, window.innerWidth);

      if (wasInit && !shouldInit) {
        this.destroy();
        return;
      }
      if (!wasInit && shouldInit) {
        this._build();
        return;
      }
      if (wasInit) {
        this.settings = mergeSettings(this.userOptions, window.innerWidth);
        this.refresh();
      }
    }

    _teardown(restore) {
      if (!this.root.classList.contains(RM.initialized)) return;

      if (this.prevArrow) this.prevArrow.remove();
      if (this.nextArrow) this.nextArrow.remove();
      if (this.dotsNav) this.dotsNav.remove();
      if (this.list) {
        this.list.removeEventListener("pointerdown", this._onPointerDown);
        this.list.remove();
      }

      if (restore && this.originalChildNodes) {
        this.root.innerHTML = "";
        this.originalChildNodes.forEach((node) => this.root.appendChild(node));
      }

      this.root.classList.remove(RM.initialized, "carousel", RM.variableWidth);
      this.list = null;
      this.track = null;
    }

    destroy() {
      window.removeEventListener("resize", this._onResize);
      this._teardown(true);
      registry.delete(registryKey(this.root));
      if (this.navPartner && this.navPartner.navPartner === this) {
        this.navPartner.navPartner = null;
      }
    }
  }

  Carousel.instances = registry;

  Carousel.refreshAll = function (selector) {
    resolveElements(selector).forEach((el) => {
      const instance = registry.get(registryKey(el));
      if (instance) instance.refresh();
    });
  };

  Carousel.initAll = function (configs) {
    configs.forEach((cfg) => {
      const elements = resolveElements(cfg.selector);
      const options = cfg.options || {};
      if (cfg.enableBelow != null) options.enableBelow = cfg.enableBelow;
      elements.forEach((el) => new Carousel(el, options));
    });
  };

  theme.Carousel = Carousel;
})();
